import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';

export const propertyRoleService = {
  getAllRoles,
  getRoleSummary,
  assignUsersToRole,
  addUserToRole,
  removeUserFromRole,
  getRoleTypes,
  checkUserRole,
};

/**
 * Get all role assignments for a property
 * @param {number} propertyId
 * @returns {Promise}
 */
function getAllRoles(propertyId) {
  return axios
    .get(apiEndpointFunction.propertyRoles(), {
      headers: authHeader(),
      params: { property_id: propertyId }
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { roles: [] };
    })
    .catch(err => {
      console.error('Error fetching property roles:', err);
      throw err;
    });
}

/**
 * Get role assignment summary (classifiers only)
 * @param {number} propertyId
 * @returns {Promise}
 */
function getRoleSummary(propertyId) {
  return axios
    .get(`${apiEndpointFunction.propertyRoles()}/summary`, {
      headers: authHeader(),
      params: { property_id: propertyId }
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { classifiers: {} };
    })
    .catch(err => {
      console.error('Error fetching role summary:', err);
      throw err;
    });
}

/**
 * Assign users to a role (replaces existing assignments)
 * @param {number} propertyId
 * @param {string} roleType
 * @param {array} userIds - Array of user IDs
 * @returns {Promise}
 */
function assignUsersToRole(propertyId, roleType, userIds) {
  return axios
    .put(apiEndpointFunction.propertyRoles(roleType), {
      user_ids: userIds,
      property_id: propertyId
    }, {
      headers: authHeader()
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    })
    .catch(err => {
      console.error('Error assigning users to role:', err);
      throw err;
    });
}

/**
 * Add a single user to a role (without removing existing)
 * @param {number} propertyId
 * @param {string} roleType
 * @param {number} userId
 * @returns {Promise}
 */
function addUserToRole(propertyId, roleType, userId) {
  return axios
    .post(`${apiEndpointFunction.propertyRoles(roleType)}/users`, {
      user_id: userId,
      property_id: propertyId
    }, {
      headers: authHeader()
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    })
    .catch(err => {
      console.error('Error adding user to role:', err);
      throw err;
    });
}

/**
 * Remove a user from a role
 * @param {number} propertyId
 * @param {string} roleType
 * @param {number} userId
 * @returns {Promise}
 */
function removeUserFromRole(propertyId, roleType, userId) {
  return axios
    .delete(`${apiEndpointFunction.propertyRoles(roleType)}/users/${userId}`, {
      params: { property_id: propertyId },
      headers: authHeader()
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    })
    .catch(err => {
      console.error('Error removing user from role:', err);
      throw err;
    });
}

/**
 * Get available role types
 * @returns {Promise}
 */
function getRoleTypes() {
  return axios
    .get(`${apiEndpoint.propertyRoles}/role-types`, {
      headers: authHeader()
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { role_types: [] };
    })
    .catch(err => {
      console.error('Error fetching role types:', err);
      throw err;
    });
}

/**
 * Check if a user has a specific role
 * @param {number} propertyId
 * @param {number} userId
 * @param {string} roleType
 * @returns {Promise}
 */
function checkUserRole(propertyId, userId, roleType) {
  return axios
    .get(`${apiEndpointFunction.propertyRoles()}/check`, {
      params: { user_id: userId, role_type: roleType, property_id: propertyId },
      headers: authHeader()
    })
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return { has_role: false };
    })
    .catch(err => {
      console.error('Error checking user role:', err);
      throw err;
    });
}
